/* eslint-disable max-lines */
import { createSelector } from 'reselect';
import toLower from 'lodash/toLower';
import isEmpty from 'lodash/isEmpty';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  findCategoryFilter,
  findCategoryFilterData,
} from '@tcp/core/src/components/features/browse/ProductListing/container/PlpCarousel.utils';
import { FACETS_FIELD_KEY } from '@tcp/core/src/services/abstractors/productListing/productListing.utils';
import { getShopThisStoreParams, getUrlParameter } from '@tcp/core/src/utils';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import {
  getIsGuest,
  isPlccUser,
  isRememberedUser,
} from '../../../account/User/container/User.selectors';
import { PLP_ROUTE } from '../../../../../constants/plp.constants';
import {
  isShopThisStoreModeOn,
  getIsNewSBCImplementationEnabled,
  getIsSEOCarouselShiftingAllowed,
  getIsHidePlpGridAndFiltersEnabled,
} from '../../../../../reduxStore/selectors/session.selectors';
import { SHOP_THIS_STORE_URL_QUERY_KEY } from '../../../../../constants/shopThisStore.constant';
import { getLocalStorage } from '../../../../../utils/localStorageManagement';
import { getStoreFilterData } from '../../ShopThisStore/container/ShopThisStore.utils';

// Adding at the top as the util requiring this label is used only in web, in case we want to use this in the app as well move this label in the function where this is used.
const shopAllLabel = getLabelFromCopyOrFromTranslation('PLP.plpTiles.lbl_shop_all', PLPLabels);

const getSortedl2Group = (l1SubCategories = {}) => {
  const level2Groups = Object.values(l1SubCategories);
  return level2Groups.sort((prevGroup, curGroup) => {
    return prevGroup.order - curGroup.order;
  });
};

const getShopThisStoreL1categories = (state) => {
  return state?.ShopThisStore?.shopThisStoreL1categories;
};

const getShopThisStoreL2categories = (state) => {
  return state?.ShopThisStore?.shopThisStoreL2categories;
};

const getFirstActiveSubCategory = (categories) => {
  return categories.find((cat) => cat?.categoryContent?.displayToCustomer === true);
};
// eslint-disable-next-line sonarjs/cognitive-complexity
export const getCurrentNavCat = (navData, catId) => {
  const categoryFound = [];
  if (!Array.isArray(navData)) return categoryFound;

  try {
    navData.some((l1) => {
      if (l1.url === `/c?cid=${catId}` && !l1?.categoryContent?.linkedCategory) {
        const subCategories = [];
        const l2groupSorted = getSortedl2Group(l1.subCategories || []);
        l2groupSorted.forEach((l2group) => {
          l2group.items?.forEach((subCat) => {
            if (subCat?.categoryContent?.displayToCustomer) {
              subCategories.push({
                asPath: subCat.asPath,
                categoryContent: subCat.categoryContent,
                hasL3: subCat.hasL3,
                subCategories: subCat.hasL3
                  ? getFirstActiveSubCategory(subCat?.subCategories || [])
                  : {},
              });
            }
          });
        });
        categoryFound.push({ ...l1.categoryContent, catDepth: 'l1', subCategories });
        return true;
      }
      const l2groupSort = getSortedl2Group(l1.subCategories || []);
      const l1CatFound = l2groupSort.some((l2group) => {
        return (
          Array.isArray(l2group.items) &&
          l2group.items?.some((l2) => {
            if (l2.url === `/c?cid=${catId}` && !l2?.categoryContent?.linkedCategory) {
              categoryFound.push({
                ...l2.categoryContent,
                subCategories: l2.subCategories,
                catDepth: 'l2',
                hasL3: l2.hasL3 > 0,
              });
              return true;
            }
            const l2CatFound =
              Array.isArray(l2.subCategories) &&
              l2.subCategories?.some((l3) => {
                if (l3.url === `/c?cid=${catId}` && !l3?.categoryContent?.linkedCategory) {
                  categoryFound.push({ ...l3.categoryContent, catDepth: 'l3' });
                  return true;
                }
                return false;
              });
            if (l2CatFound) {
              categoryFound.unshift({ ...l2.categoryContent, catDepth: 'l2' });
            }
            return l2CatFound;
          })
        );
      });
      if (l1CatFound) {
        categoryFound.unshift({ ...l1.categoryContent, catDepth: 'l1' });
      }
      return l1CatFound;
    });
  } catch (e) {
    logger.error({
      error: `Error in Categorylistingpage selectors - ${e}`,
      extraData: {
        componentName: 'getCurrentNavCat',
        categoryId: catId,
      },
    });
  }
  return categoryFound;
};

export const getCurrentNavIds = (state) => state.CategoryListingPage.navcurrentNavIds;

export const getCurrentNavId = (state) => {
  const currentNavigationIds =
    state.CategoryListingPage && state.CategoryListingPage.navcurrentNavIds;
  return currentNavigationIds && currentNavigationIds[currentNavigationIds.length - 1];
};

export const getCategoryListingPageState = (state) => state?.CategoryListingPage;

export const getNavigationData = (state) => {
  return state?.Navigation.navigationData;
};

export const getCategoryProducts = (state) => {
  return state.CategoryListingPage.paginatedPlpProducts;
};

export const getCategoryProductsLength = (state) => {
  return state.CategoryListingPage.paginatedPlpProducts?.length;
};

export const getTotalProductsCount = (state) => {
  return state.CategoryListingPage.totalProductsCount;
};

export const getIsCmsCarouselAvailable = createSelector(
  (state) => state.CategoryListingPage,
  (categoryListingPage) => categoryListingPage && categoryListingPage?.isCmsCarouselAvailable
);

export const getIsLayoutFetched = createSelector(
  (state) => state.Layouts?.productListingPage,
  (productListingPage) => productListingPage && productListingPage?.fetched
);

export const getNavigation = (state) => {
  return state?.Navigation?.navigationData;
};

const getBreadCrumbsCache = {};
export const getBreadCrumbs = (cid) => {
  if (!getBreadCrumbsCache[cid]) {
    getBreadCrumbsCache[cid] = createSelector(getNavigation, (navigation) => {
      return getCurrentNavCat(navigation, cid);
    });
  }
  return getBreadCrumbsCache[cid];
};

export const getCurrentPageNumber = (state) => {
  return state.CategoryListingPage.currentPageNumber || 1;
};

export const getCategoryProductsByPage = createSelector(
  [getCategoryProducts, getCurrentPageNumber],
  (plpProducts, page) => {
    return plpProducts[page - 1];
  }
);

export const getUnbxdCallByPageNumber = (state) => {
  return state.CategoryListingPage.unbxdCallByPageNumber;
};

export const getSpecificPlpTileData = (tileId) =>
  createSelector(getCategoryProducts, (products) => {
    return products[tileId];
  });

export const computedBreadCrumbs = (state) => state.CategoryListingPage?.breadCrumbs;

export const getIsShortImage = createSelector(computedBreadCrumbs, (breadCrumbs = []) => {
  if (breadCrumbs.length > 0) {
    return !!breadCrumbs[breadCrumbs.length - 1].isShortImage;
  }
  return false;
});

export const getIsHidePlpGridAndFilters = createSelector(
  computedBreadCrumbs,
  getIsHidePlpGridAndFiltersEnabled,
  (breadCrumbs = [], isHidePlpGridAndFilters) => {
    if (breadCrumbs.length > 0 && isHidePlpGridAndFilters) {
      const lastBreadcrumb = breadCrumbs[breadCrumbs.length - 1];
      return !!lastBreadcrumb?.mainCategory?.hidePlpGridAndFilters;
    }
    return false;
  }
);

export const getIsL2Category = (state) => {
  return state.CategoryListingPage.isL2Category;
};

export const getTitleDataPerPage = createSelector(
  [getUnbxdCallByPageNumber, getCurrentPageNumber, getIsL2Category],
  (paginationObj, page, isL2Category) => {
    if (!isL2Category) return 0;
    const catTitleData = paginationObj && paginationObj[page - 1];
    let titleIndex = 0;
    if (Array.isArray(catTitleData)) {
      titleIndex = catTitleData.length;
    } else if (typeof catTitleData === 'object') {
      titleIndex = 1;
    }
    return titleIndex;
  }
);

export const getFilterMaps = (state) => {
  return state?.CategoryListingPage?.filtersMaps;
};

export const getCategoryModuleFilters = (state) => {
  return state?.CategoryListingPage?.catModulesFilters;
};

export const getQuickFilters = (state) => state?.CategoryListingPage?.quickFilters;

export const fetchBreadCrumbs = createSelector(
  computedBreadCrumbs,
  getShopThisStoreL1categories,
  getShopThisStoreL2categories,
  (breadCrumb = [], l1Data = [], l2Data = []) => {
    const finalBreadCrumb = [
      {
        destination: PLP_ROUTE,
        displayName: 'Home',
        pathSuffix: 'home',
      },
    ];
    let params = '';
    let shopThisStoreQueryParam = '';
    if (isShopThisStoreModeOn()) {
      const defaultStore = getLocalStorage('defaultStore')
        ? JSON.parse(getLocalStorage('defaultStore'))
        : {};
      params = !isEmpty(defaultStore) ? getShopThisStoreParams(defaultStore?.basicInfo) : '';
      shopThisStoreQueryParam = `?bopisStoreId=${getStoreFilterData()}&mode=${getUrlParameter(
        'mode'
      )}`;
    }
    breadCrumb?.forEach((bc) => {
      finalBreadCrumb.push({
        categoryId: isShopThisStoreModeOn() ? '' : bc.id,
        destination: isShopThisStoreModeOn() ? SHOP_THIS_STORE_URL_QUERY_KEY : PLP_ROUTE,
        displayName: bc.name,
        pathSuffix:
          isShopThisStoreModeOn() && l1Data?.includes(bc?.name)
            ? `${SHOP_THIS_STORE_URL_QUERY_KEY}?storeNameId=${params}&category=${
                bc?.name
              }&categoryGrouping=${
                l2Data?.length ? l2Data[0] : ''
              }&mode=${SHOP_THIS_STORE_URL_QUERY_KEY}&from=breadcrumbs`
            : `${PLP_ROUTE}?cid=${bc.seoToken}${shopThisStoreQueryParam}`,
      });
    });
    return finalBreadCrumb;
  }
);

export const getDefaultStore = (state) => {
  return state.User.get('defaultStore');
};

export const getIsBopisFilterOn = (state) => {
  return state?.CategoryListingPage?.isBopisFilterOn;
};

export const getIsPaginatedPlpLoading = (state) => {
  return state.CategoryListingPage.isPaginatedPlpLoading;
};

export const getAppliedFilters = (state) => {
  return state.CategoryListingPage.appliedFilters;
};
export const getFiltersFromURL = (state) => {
  return state.CategoryListingPage.filtersFromURL;
};

export const getAppliedSortId = (state) => {
  return state.CategoryListingPage.appliedSortId;
};

export const getLongDescription = (state) => {
  return state.CategoryListingPage.longDescription;
};

export const getInitialValues = createSelector(
  getAppliedFilters,
  getAppliedSortId,
  (appliedFilters, appliedSortId) => {
    return {
      ...appliedFilters,
      sort: appliedSortId || '',
    };
  }
);

export const getAppliedFilterIds = createSelector(getAppliedFilters, (appliedFiltersIds = {}) => {
  const filtersLength = {};
  let filterCount = 0;

  Object.keys(appliedFiltersIds).forEach((key) => {
    if (appliedFiltersIds[key]) {
      const keysLength = appliedFiltersIds[key].length;
      filtersLength[`${key}Filters`] = keysLength;
      filterCount += keysLength;
    }
  });

  return {
    filtersLength,
    filterCount,
  };
});
function processFilterValue(filterValue) {
  let filter = filterValue;
  if (filter && filter.includes('#')) {
    const [, valueAfterHash] = filter.split('#');
    filter = valueAfterHash;
  }
  return filter;
}

const updateFiltersData = (filters, isFromURL = false) => {
  const filtersLength = {};
  let filterCount = 0;
  let filterCountFromURL = 0;
  let singleColorFilterValue = '';
  let singleSizeFilterValue = '';
  Object.keys(filters).forEach((key) => {
    if (key && filters[key] && key !== 'page') {
      const keysLength = filters[key].length;
      filtersLength[key] = keysLength;

      if (isFromURL) {
        filterCountFromURL += keysLength;
      } else {
        filterCount += keysLength;
      }

      if (keysLength === 1) {
        const filterValue = filters[key][0];
        if (key.toLowerCase() === FACETS_FIELD_KEY.color) {
          singleColorFilterValue = filterValue;
        } else if (
          [
            FACETS_FIELD_KEY.clothingsize,
            FACETS_FIELD_KEY.cleanClothingSize.toLowerCase(),
          ].includes(key.toLowerCase())
        ) {
          singleSizeFilterValue = filterValue.toUpperCase();
        }
      }
    }
  });
  return {
    filtersLength,
    filterCount,
    filterCountFromURL,
    singleColorFilterValue,
    singleSizeFilterValue: processFilterValue(singleSizeFilterValue),
  };
};

export const getAppliedFilterIdsAndLength = createSelector(
  getAppliedFilters,
  getFiltersFromURL,
  (appliedFiltersIds = {}, filtersFromURL = {}) => {
    const {
      filtersLength = {},
      filterCount = 0,
      filterCountFromURL = 0,
      singleColorFilterValue = '',
      singleSizeFilterValue = '',
    } = updateFiltersData(appliedFiltersIds);

    if (!filterCount) {
      return updateFiltersData(filtersFromURL, true);
    }

    return {
      filtersLength,
      filterCount,
      filterCountFromURL,
      singleColorFilterValue,
      singleSizeFilterValue,
    };
  }
);

export const isCachedTimeOver = (state) => {
  const currentTimeInMs = Date.now();
  const { defaultCacheTime, cachedAt } = state.CategoryListingPage;
  return cachedAt ? currentTimeInMs - cachedAt >= defaultCacheTime : true;
};

export const pagesToBeCached = (state) => {
  return state.CategoryListingPage.pagesToBeCached; // should come from session selector.
};

export const getIsAlternatePlpEnabled = (state) => {
  return state.CategoryListingPage.isAlternatePlpEnabled;
};

export const getIsHorizontalPlpEnabled = (state) => {
  return state.CategoryListingPage.isHorizontalPlpEnabled;
};

export const getProductLoadingFlag = (state) => {
  return state.CategoryListingPage.isProductLoadingFlag;
};

export const getIsPaginatedAlternatePlpEnabled = (state) => {
  return state.CategoryListingPage.isPaginatedAlternatePlpEnabled;
};
export const getCanonicalUrl = (state) => {
  return state.CategoryListingPage.canonicalUrl;
};

export const getSeoMetaDesc = (state) => {
  return state.CategoryListingPage.seoMetaDesc;
};

export const getSeoTitle = (state) => {
  return state.CategoryListingPage.seoTitle;
};

export const getSeoPageDirective = (state) => {
  return state.CategoryListingPage.seoPageDirective;
};

export const getSeoTokens = (state) => {
  return state.CategoryListingPage.plpSeoToken;
};

export const getIsInitialCall = (state) => {
  return state.CategoryListingPage.isInitialCall;
};

export const getIsFromHplp = (state) => {
  return state.CategoryListingPage.fromHplp;
};

export const getUpdatedUrl = (state) => {
  return state.CategoryListingPage.updatedUrl;
};

export const getIsShowL2Skeleton = (state) => {
  return state.CategoryListingPage.isShowL2Skeleton;
};

export const getCategoryId = (state) => {
  const currentNavigationIds = state.CategoryListingPage && state.CategoryListingPage.currentNavIds;
  return currentNavigationIds && currentNavigationIds[currentNavigationIds.length - 1];
};

export const getSeoHeaderCopyText = (state) => {
  return state.CategoryListingPage.seoHeaderCopy;
};

export const getSeoHeaderCopyTextLoading = (state) => {
  return state.CategoryListingPage.isSeoHeaderCopyLoaded;
};

const findIfHasPlccOrMpr = (promos) => {
  const userTypeObj = {
    hasPlcc: false,
    hasMpr: false,
  };
  promos?.forEach((promo) => {
    if (promo?.slotNo) {
      if (promo?.name === 'mpr') {
        userTypeObj.hasMpr = true;
      } else if (promo.name === 'plcc') {
        userTypeObj.hasPlcc = true;
      }
    }
  });
  return userTypeObj;
};

const getPromoModuleInfo = ({ promoItem, modules, type }) => {
  let promoModuleInfo = (promoItem.contentId && modules[promoItem.contentId]) || {};
  if (type === 'grid' || type === 'horizontal') {
    promoModuleInfo = {
      ...promoModuleInfo,
      slot: promoItem && promoItem.slotNo,
      ...(!isEmpty(promoModuleInfo) && { aspectRatio: promoItem && promoItem?.aspectRatio }),
    };
  }
  return promoModuleInfo;
};

const getMprPromo = ({ isGuest, isRemembered, isPlcc, promoModuleInfo, aspectRatio }) => {
  if ((!isGuest || isRemembered) && !isPlcc) {
    return { ...promoModuleInfo, aspectRatio };
  }
  return {};
};

const getPlccPromo = ({ isPlcc, promoModuleInfo, aspectRatio }) => {
  if (isPlcc) {
    return { ...promoModuleInfo, aspectRatio };
  }
  return {};
};

const getGuestPromo = ({
  userTypeObj,
  isGuest,
  isPlcc,
  isRemembered,
  promoModuleInfo,
  aspectRatio,
}) => {
  if (
    (!userTypeObj?.hasPlcc && isPlcc) ||
    (!userTypeObj?.hasMpr && !isGuest && !isPlcc) ||
    (isGuest && !isRemembered)
  ) {
    return { ...promoModuleInfo, aspectRatio };
  }
  return {};
};

const getUserBasedPLPPromo = ({
  promoItem,
  modules,
  isGuest,
  isPlcc,
  type,
  userTypeObj,
  isRemembered,
}) => {
  const promoModuleInfo = getPromoModuleInfo({ promoItem, modules, type });
  const aspectRatio = promoItem?.aspectRatio;

  switch (toLower(promoItem.name)) {
    case 'mpr':
      return getMprPromo({ isGuest, isRemembered, isPlcc, promoModuleInfo, aspectRatio });
    case 'plcc':
      return getPlccPromo({ isPlcc, promoModuleInfo, aspectRatio });
    case 'guest':
      return getGuestPromo({
        userTypeObj,
        isGuest,
        isPlcc,
        isRemembered,
        promoModuleInfo,
        aspectRatio,
      });
    default:
      return {};
  }
};

const flattenPromoSet = (promo) => {
  const promoSet = {};
  if (promo.set && Array.isArray(promo.set)) {
    promo.set.forEach((i) => {
      promoSet[i.key] = i.val;
    });
  }
  return promoSet;
};

const getPLPIngridPromoCondition = (promoItem, isGuest, isPlcc) => {
  const { guest, mpr, plcc } = flattenPromoSet(promoItem);

  return (guest && isGuest) || (plcc && isPlcc) || (!isGuest && !isPlcc && mpr);
};

export const getLayoutProductListingPageData = createSelector(
  (state) => state?.Layouts?.productListingPage,
  (layoutData) => {
    return layoutData || {};
  }
);

export const getHplpLayoutData = createSelector(
  (state) => state?.Layouts?.hplp,
  (layoutData) => {
    return layoutData || {};
  }
);

export const getCidPath = (state) => {
  const breadCrumbs = computedBreadCrumbs(state) || [];
  if (breadCrumbs.length > 0) {
    return breadCrumbs[breadCrumbs.length - 1].seoToken;
  }
  return null;
};

export const getIfCategoryHasCMSPromoCombinations = createSelector(
  (state) => state?.PromoCombinations,
  getCidPath,
  (promoCombinations, categoryPath) => {
    return !isEmpty(promoCombinations) && !isEmpty(promoCombinations[categoryPath]);
  }
);

export const getTopPromosContent = createSelector(
  (state) => state?.Layouts?.productListingPage?.top,
  (state) => state?.Layouts?.productListingPage?.loyaltyBanner,
  (state) => state?.Modules,
  (state) => state?.PromoCombinations,
  getIsGuest,
  isPlccUser,
  isRememberedUser,
  getCidPath,
  getIsSEOCarouselShiftingAllowed,
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  (
    topPromosParam,
    loyaltyPromoParam,
    modules,
    promoCombinations,
    isGuest,
    isPlcc,
    isRemembered,
    categoryKey,
    isSEOCarouselShiftingAllowed
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    let topPromos = topPromosParam;

    if (!isEmpty(promoCombinations) && categoryKey) {
      const newTopPromos = promoCombinations[categoryKey]?.top;

      if (!isEmpty(newTopPromos)) {
        topPromos = newTopPromos;
      }
    }

    let loyaltyPromo = loyaltyPromoParam;

    if (!isEmpty(promoCombinations) && categoryKey) {
      const newLoyaltyPromos = promoCombinations[categoryKey]?.loyalty;

      if (!isEmpty(newLoyaltyPromos)) {
        loyaltyPromo = newLoyaltyPromos;
      }
    }

    const loyaltyPromos =
      (loyaltyPromo &&
        loyaltyPromo.map((loyalPromo) => {
          const loyalPromoModule = loyalPromo.contentId && modules[loyalPromo.contentId];
          if (loyalPromoModule) {
            loyalPromoModule.userType = loyalPromo.name;
          }
          return loyalPromoModule || {};
        })) ||
      [];

    const userTypeObj = findIfHasPlccOrMpr(topPromos);
    const promos =
      (topPromos &&
        topPromos.map((promoItem) => {
          if (promoItem.slotNo) {
            return getUserBasedPLPPromo({
              promoItem,
              modules,
              isGuest,
              isPlcc,
              isRemembered,
              userTypeObj,
            });
          }
          if (getPLPIngridPromoCondition(promoItem, isGuest, isPlcc)) {
            return {};
          }
          return (promoItem.contentId && modules[promoItem.contentId]) || {};
        })) ||
      [];
    const allTopPromos = loyaltyPromos.concat(promos).filter((item) => !isEmpty(item));
    if (isSEOCarouselShiftingAllowed && allTopPromos) {
      try {
        const outfitIndex = allTopPromos.findIndex((item) => item.moduleName === 'outfitCarousel');
        if (outfitIndex !== -1) {
          const mediaLinkedList = allTopPromos[outfitIndex]?.mediaLinkedList;
          if (Array.isArray(mediaLinkedList) && mediaLinkedList.length) {
            const index = mediaLinkedList.findIndex((obj) => {
              return obj?.link?.url?.split('?')?.[0] === `/c/${categoryKey}`;
            });
            if (index !== -1) {
              const element = mediaLinkedList.splice(index, 1);
              mediaLinkedList.unshift(...element);
              allTopPromos[outfitIndex].mediaLinkedList = mediaLinkedList;
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    return allTopPromos.length > 0 ? allTopPromos : [];
  }
);

function shiftObjectToFirst(array, key, value) {
  try {
    if (Array.isArray(array) && array.length > 0) {
      const index = array.findIndex((obj) => obj[key]?.split('=')[1] === value);
      if (index !== -1) {
        const element = array.splice(index, array.length - index);
        array.unshift(...element);
      }
    }
    return array;
  } catch (error) {
    console.error(error);
    return [];
  }
}

const getCarouselObj = (isSEOCarouselShiftingAllowed, carouselObj, categoryKey) => {
  return isSEOCarouselShiftingAllowed
    ? shiftObjectToFirst(carouselObj, 'linkUrl', categoryKey)
    : carouselObj;
};

const checkSBC = (
  isSBCNewImplementationEnabled,
  categoryKey,
  navigationState,
  currentNavIds,
  isSEOCarouselShiftingAllowed
) => {
  let categoryCarouselList;
  let carouselTitle = '';
  let carouselObj = null;
  if (isSBCNewImplementationEnabled) {
    categoryCarouselList =
      currentNavIds?.length > 0
        ? findCategoryFilterData(navigationState, currentNavIds, shopAllLabel)
        : [];
    carouselObj = categoryCarouselList[0]?.carouselData;
    carouselTitle = categoryCarouselList[0]?.carouselTitle || '';
  } else {
    categoryCarouselList = categoryKey
      ? findCategoryFilter(navigationState, categoryKey, shopAllLabel).reverse()
      : [];
    if (categoryCarouselList.length > 2) {
      carouselObj = categoryCarouselList[2].carouselData;
    }
    if (categoryCarouselList.length > 1 && !carouselObj) {
      carouselObj = categoryCarouselList[1].carouselData;
    }
    carouselTitle =
      categoryCarouselList.length > 1 ? categoryCarouselList[1].carouselTitle || '' : '';
  }
  carouselObj = getCarouselObj(isSEOCarouselShiftingAllowed, carouselObj, categoryKey);
  return { carouselData: carouselObj, carouselTitle };
};

export const getGenericCarousel = createSelector(
  getCidPath,
  (state) => state.Navigation.navigationData,
  (state) => state.CategoryListingPage.navcurrentNavIds,
  getIsNewSBCImplementationEnabled,
  getIsSEOCarouselShiftingAllowed,
  (
    categoryKey,
    navigationState,
    currentNavIds,
    isSBCNewImplementationEnabled,
    isSEOCarouselShiftingAllowed
  ) => {
    return checkSBC(
      isSBCNewImplementationEnabled,
      categoryKey,
      navigationState,
      currentNavIds,
      isSEOCarouselShiftingAllowed
    );
  }
);

export const getCategoryArrayForRecomendation = (state) => {
  const { CategoryListingPage } = state;
  return CategoryListingPage &&
    CategoryListingPage?.currentNavIds &&
    Array.isArray(CategoryListingPage?.currentNavIds) &&
    CategoryListingPage?.currentNavIds?.length > 0
    ? CategoryListingPage?.currentNavIds?.map((item) => item).join('>')
    : '';
};

export const getIsServiceError = (state) => {
  return state.CategoryListingPage.serviceErrorFlag;
};

export const getIsPromoCallCompletedForCategory = (state) => {
  const cidPath = getCidPath(state);
  return state.PromoCombinations[cidPath]?.isCompleted;
};

export const getIsPromoCombinationHasTopPromos = (state) => {
  const cidPath = getCidPath(state);
  return state.PromoCombinations[cidPath]?.hasTopPromos;
};

export const getAspectRatioForCategory = createSelector(computedBreadCrumbs, (breadCrumbs = []) => {
  let breadCrumbItem = {};
  if (breadCrumbs.length > 0) {
    breadCrumbItem = breadCrumbs[breadCrumbs.length - 1];
  }
  return breadCrumbItem?.mainCategory?.categoryAspectRatio;
});

export const getIsStageHoldCategoriesOosMessage = createSelector(
  getCategoryListingPageState,
  (categoryListingPageState) => categoryListingPageState?.isStageHoldCategory
);

export const getPriceFilterFLag = createSelector(
  getCategoryListingPageState,
  (categoryListingPageState) => categoryListingPageState?.priceFilterFlag
);

export const getSubCatAppliedFilters = (state) => {
  return state.CategoryListingPage.appliedSubCatFilters;
};

export const getSubCatFacetList = (state) => {
  return state.CategoryListingPage.subCatFacetList;
};

export const getSubCatFilterMaps = (state) => {
  return state.CategoryListingPage.subCatFilterMaps;
};

export const getSubCatFilterMapsLoaded = (state) => {
  return state?.CategoryListingPage?.isSubCatFilterMapsLoaded;
};

export const getIsSubCategoryFilterLoading = (state) => {
  return state.CategoryListingPage.isSubCategoryFilterLoading;
};

export const getIsBucketLoadingFlag = (state) => {
  return state.CategoryListingPage.isBucketLoading;
};
